import { ACTIVITY_LANGUAGES, PARTICIPATIONS } from '@yoyoclub/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AgeInput } from './AgeInput';
import { CheckboxSection } from './filterControls';
import { ageRangeLabel, whenPresetLabel, WHEN_PRESETS } from './filterLabels';
import {
  EMPTY_FILTERS,
  FilterState,
  toggled,
  withAge,
  withParticipationToggled,
} from './filterState';

const CATEGORY_VALUES = [
  'ART',
  'MUSIC',
  'DANCE',
  'SPORTS',
  'COOKING',
  'STEM',
  'NATURE',
  'OTHER',
] as const;
const REGION_VALUES = ['SOUTH_BAY', 'PENINSULA', 'EAST_BAY', 'SAN_FRANCISCO', 'OTHER'] as const;

interface Props {
  open: boolean;
  value: FilterState;
  onClose: () => void;
  onApply: (next: FilterState) => void;
}

/**
 * Mobile bottom sheet hosting the same filter controls as the desktop bar.
 * Local draft state — only commits via "Apply" so users can cancel partial edits.
 */
export default function ActivitiesFiltersSheet({ open, value, onClose, onApply }: Props) {
  const { t } = useTranslation('activities');
  const { t: tCommon } = useTranslation('common');
  const [draft, setDraft] = useState<FilterState>(value);

  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  // Prevent background scroll while sheet is open.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  const set = (patch: Partial<FilterState>) => setDraft((d) => ({ ...d, ...patch }));
  const setAge = (age: string) => setDraft((d) => withAge(d, age));

  return (
    <div className="fixed inset-0 z-50 flex flex-col">
      <button type="button" aria-label="close" className="flex-1 bg-black/40" onClick={onClose} />
      <div className="bg-white rounded-t-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-3 flex items-center justify-between flex-shrink-0 relative">
          <button onClick={onClose} className="text-sm text-gray-500">
            {t('filters.cancel')}
          </button>
          <div className="font-semibold text-sm">{t('filters.mobileButtonNone')}</div>
          <button
            onClick={() => setDraft(EMPTY_FILTERS)}
            className="text-sm text-primary font-semibold"
          >
            {t('filters.clearAll')}
          </button>
          <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-10 h-1 bg-soft-gray rounded-full" />
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-5 py-4 flex-1 space-y-6">
          {/* When */}
          <section>
            <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
              {t('filters.when')}
            </h3>
            <div className="space-y-1">
              {WHEN_PRESETS.map((p) => (
                <label key={p} className="flex items-center gap-3 py-2.5 cursor-pointer">
                  <input
                    type="radio"
                    name="m-when"
                    checked={draft.whenPreset === p}
                    onChange={() => set({ whenPreset: p })}
                    className="accent-primary"
                  />
                  <span className={`text-sm ${draft.whenPreset === p ? 'font-semibold' : ''}`}>
                    {whenPresetLabel({ whenPreset: p, dateFrom: '', dateTo: '' }, t)}
                  </span>
                </label>
              ))}
              {draft.whenPreset === 'custom' && (
                <div className="pt-2 space-y-2">
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500 w-6 shrink-0">{t('when.from')}</label>
                    <input
                      type="date"
                      value={draft.dateFrom}
                      onChange={(e) => {
                        const next = e.target.value;
                        // No native `max` — clamp dateTo forward so the range can shift freely.
                        const clampedTo = draft.dateTo && next > draft.dateTo ? next : draft.dateTo;
                        set({ dateFrom: next, dateTo: clampedTo });
                      }}
                      className="flex-1 min-w-0 text-sm px-2 py-1.5 border border-soft-gray rounded-lg accent-primary"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500 w-6 shrink-0">{t('when.to')}</label>
                    <input
                      type="date"
                      value={draft.dateTo}
                      min={draft.dateFrom || undefined}
                      onChange={(e) => set({ dateTo: e.target.value })}
                      className="flex-1 min-w-0 text-sm px-2 py-1.5 border border-soft-gray rounded-lg accent-primary"
                    />
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Age: a child's age; a homepage stage range shows here until an age replaces it */}
          <section>
            <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
              {t('filters.age')}
            </h3>
            {draft.ageRange && (
              <button
                type="button"
                onClick={() => set({ ageRange: null })}
                className="mb-3 inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-primary text-primary rounded-full text-sm"
              >
                {ageRangeLabel(draft.ageRange, t)}
                <span aria-hidden="true">✕</span>
              </button>
            )}
            <div className="text-xs text-gray-500 mb-2">{t('filters.childAge')}</div>
            <AgeInput value={draft.age} onChange={setAge} />
          </section>

          {/* Region */}
          <CheckboxSection
            title={t('filters.region')}
            options={REGION_VALUES.map((r) => ({
              value: r,
              label: tCommon(`region.${r}` as never),
            }))}
            selected={draft.regions}
            onToggle={(r) => set({ regions: toggled(draft.regions, r) })}
          />

          {/* Category */}
          <section>
            <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
              {t('filters.category')}
            </h3>
            <div className="flex flex-wrap gap-2">
              {CATEGORY_VALUES.map((c) => {
                const selected = draft.categories.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => set({ categories: toggled(draft.categories, c) })}
                    className={`px-3 py-1.5 text-sm rounded-full border ${
                      selected
                        ? 'bg-primary text-white border-primary'
                        : 'border-soft-gray text-charcoal'
                    }`}
                  >
                    {tCommon(`category.${c}` as never)}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Language */}
          <CheckboxSection
            title={t('filters.language')}
            options={ACTIVITY_LANGUAGES.map((l) => ({
              value: l,
              label: tCommon(`teachingLanguage.${l}` as never),
            }))}
            selected={draft.languages}
            onToggle={(l) => set({ languages: toggled(draft.languages, l) })}
          />

          {/* Participation (replaces the old Adult toggle) */}
          <CheckboxSection
            title={t('filters.participation')}
            options={PARTICIPATIONS.map((p) => ({
              value: p,
              label: tCommon(`participation.${p}` as never),
            }))}
            selected={draft.participations}
            onToggle={(p) => setDraft((d) => withParticipationToggled(d, p))}
          />
        </div>

        {/* Apply footer */}
        <div className="px-5 py-3 bg-white flex-shrink-0">
          <button
            type="button"
            onClick={() => {
              onApply(draft);
              onClose();
            }}
            className="w-full bg-primary text-white rounded-full py-3 font-semibold text-sm"
          >
            {t('filters.apply')}
          </button>
        </div>
      </div>
    </div>
  );
}
